<template>
  <div class="" style="width: 100%;">
         <v-row>
      <v-col>
    <v-container fluid v-if="data">
      <v-row>
        <v-col class="d-flex flex-row align-center">
          <v-btn text class="mx-2" to="/booklists">
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <h1>{{label}} #{{data.id}}</h1>
          <v-chip class="ml-4" :color="statusColor(data.status)" dark>{{data.status}}</v-chip>
          <v-progress-circular
              indeterminate
              color="green"
              v-if="loader"
              style="margin-left: 10px;"
          ></v-progress-circular>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-card-text>
              <VarionScannerButton target-type="booklist" :target-id="data.id" label="Attach photo via Varion Scanner" @complete="$refs.blAttachments && $refs.blAttachments.refresh()" />
              <div class="mt-3">
                <AttachmentsPanel ref="blAttachments" target-type="booklist" :target-id="data.id" />
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="8">
          <v-row>
            <v-col cols="6">
              <v-text-field label="School" outlined dense v-model="data.schoolName"/>
            </v-col>
            <v-col cols="6">
              <v-text-field label="Grade/Class" outlined dense v-model="data.grade"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <p class="mb-1" v-if="data.requesterPhone"><b>Requested by:</b> {{data.requesterPhone}} <span v-if="data.metadata && data.metadata.profileName">({{data.metadata.profileName}})</span></p>
              <p class="mb-1" v-else><b>Created manually</b> by staff</p>
              <p class="mb-1"><b>{{data.requesterPhone ? "Requested" : "Created"}} at:</b> {{formatDate(data.createdAt)}}</p>
              <p class="mb-1" v-if="data.bApprovedBy"><b>Approved by:</b> {{data.bApprovedBy.firstName}} {{data.bApprovedBy.lastName}} at {{formatDate(data.approvedAt)}}</p>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3>Items</h3>
              <v-simple-table class="elevation-1">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width: 30%;">As Written</th>
                      <th style="width: 30%;">Matched Product</th>
                      <th style="width: 10%;">Qty</th>
                      <th style="width: 12%;">Unit Price</th>
                      <th style="width: 10%;">Line Total</th>
                      <th style="width: 8%;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in data.items" :key="i" :style="rowStyle(item)">
                      <td>
                        <v-textarea dense hide-details auto-grow rows="1" v-model="item.description"/>
                      </td>
                      <td>
                        <template v-if="item.matched">
                          {{item.productName}} <span v-if="item.sku" class="grey--text">({{item.sku}})</span>
                          <template v-if="item.confidence==='low'">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip v-bind="attrs" v-on="on" x-small color="amber darken-2" dark class="ml-1" @click="openMatchDialog(i)">verify</v-chip>
                              </template>
                              <span>The AI wasn't fully sure about this match. Click to review, or use ✓ to confirm it's correct.</span>
                            </v-tooltip>
                            <v-btn icon x-small color="success" class="ml-1" title="Confirm this match is correct" @click="confirmMatch(i)">
                              <v-icon x-small>mdi-check-bold</v-icon>
                            </v-btn>
                          </template>
                        </template>
                        <template v-else>
                          <v-chip small color="orange" dark>Unmatched</v-chip>
                          <div v-if="item.suggestions && item.suggestions.length>0" class="mt-1">
                            <div class="caption grey--text">Did you mean:</div>
                            <v-chip
                                v-for="s in item.suggestions"
                                :key="s.id"
                                x-small
                                outlined
                                color="primary"
                                class="mr-1 mb-1 suggestion-chip"
                                @click="applySuggestion(i, s)"
                            >{{s.name}} — ${{Number(s.unitPrice).toFixed(2)}}</v-chip>
                          </div>
                        </template>
                      </td>
                      <td>
                        <v-text-field dense hide-details type="number" min="1" v-model.number="item.qty"/>
                      </td>
                      <td>
                        <v-text-field dense hide-details type="number" min="0" step="0.01" v-model.number="item.unitPrice" :disabled="!item.matched"/>
                      </td>
                      <td>${{(Number(item.unitPrice||0)*Number(item.qty||0)).toFixed(2)}}</td>
                      <td class="d-flex flex-row align-center">
                        <v-btn icon small @click="openMatchDialog(i)" title="Match to a product">
                          <v-icon small>mdi-magnify</v-icon>
                        </v-btn>
                        <v-btn icon small v-if="item.matched" @click="unmatchItem(i)" title="Remove match">
                          <v-icon small>mdi-link-off</v-icon>
                        </v-btn>
                        <v-btn icon small color="error" @click="removeItem(i)" title="Remove item">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-btn class="mt-2" small @click="addItem">
                <v-icon small left>mdi-plus</v-icon>Add Item
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3>Total (matched items): ${{matchedTotal.toFixed(2)}}</h3>
              <p v-if="unmatchedCount>0" class="orange--text mb-1">{{unmatchedCount}} item(s) are unmatched and will be excluded from the quotation total.</p>
              <p v-if="lowConfCount>0" class="amber--text text--darken-3 mb-1">{{lowConfCount}} matched item(s) have low AI confidence — please verify them before approving.</p>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex flex-row">
              <v-btn class="mr-2" color="primary" @click="save" :loading="saving">Save</v-btn>
              <v-btn class="mr-2" color="success" v-if="data.status!=='approved'" @click="approveDialog=true">Approve &amp; Send Quote</v-btn>
              <v-btn class="mr-2" color="green darken-1" dark v-if="matchedCount>0" @click="openSendDialog">
                <v-icon left small>mdi-whatsapp</v-icon>Send Quote to Number
              </v-btn>
              <v-btn class="mr-2" color="secondary" v-if="isAllowed('order','c') && matchedCount>0" @click="orderDialog=true">
                <v-icon left small>mdi-cart-plus</v-icon>Generate Order
              </v-btn>
              <v-btn color="error" v-if="data.status==='pending'" @click="reject">Reject</v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="4" v-if="data.sourceImage">
          <h3>Original Image</h3>
          <a :href="`${getEndpoint}/${data.sourceImage}`" target="_blank">
            <v-img :src="`${getEndpoint}/${data.sourceImage}`" max-height="600" contain class="elevation-1"/>
          </a>
        </v-col>
      </v-row>
    </v-container>
      </v-col>
     </v-row>

    <v-dialog scrollable v-model="matchDialog" width="600">
      <v-card>
        <v-card-title class="text-h5">Match Product</v-card-title>
        <v-card-text style="padding-top: 20px;">
          <v-text-field
              label="Search products"
              outlined
              dense
              autofocus
              v-model="matchSearchTerm"
              @keydown.enter="searchProducts"
              append-icon="mdi-magnify"
              @click:append="searchProducts"
          />
          <v-progress-circular indeterminate color="green" v-if="matchLoading"/>
          <v-list dense>
            <v-list-item v-for="p in matchResults" :key="p.id" @click="selectMatch(p)">
              <v-list-item-content>
                <v-list-item-title style="white-space: normal;">{{p.name}}</v-list-item-title>
                <v-list-item-subtitle>SKU: {{p.sku || "N/A"}} — ${{Number(p.unitPrice).toFixed(2)}}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <template v-if="isAllowed('product','u')">
            <v-divider class="my-3"/>
            <p class="mb-2"><b>Can't find it?</b> Create it as a new product and match it right here:</p>
            <v-row dense>
              <v-col cols="12">
                <v-text-field label="Product name" outlined dense hide-details v-model="createProductForm.name"/>
              </v-col>
              <v-col cols="6">
                <v-text-field label="SKU (optional)" outlined dense hide-details v-model="createProductForm.sku"/>
              </v-col>
              <v-col cols="6">
                <v-text-field label="Price" outlined dense hide-details type="number" min="0" step="0.01" v-model.number="createProductForm.regularPrice"/>
              </v-col>
            </v-row>
            <v-btn
                class="mt-2"
                small
                color="primary"
                :loading="creatingProduct"
                :disabled="!createProductForm.name || !(Number(createProductForm.regularPrice)>=0)"
                @click="createAndMatch"
            >
              <v-icon small left>mdi-plus</v-icon>Create product &amp; match
            </v-btn>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeMatchDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveDialog" width="500">
      <v-card>
        <v-card-title class="text-h5">Confirm Approval</v-card-title>
        <v-card-text style="padding-top: 20px;">
          <p v-if="data && data.requesterPhone">Approving this {{label.toLowerCase()}} will save your changes and immediately send the quotation to <b>{{data.requesterPhone}}</b> on WhatsApp.</p>
          <p v-else>Approving this {{label.toLowerCase()}} will save your changes and make it available for future WhatsApp customers requesting this school and grade. This {{label.toLowerCase()}} wasn't requested by a customer, so nothing will be sent.</p>
          <p v-if="unmatchedCount>0" class="orange--text">{{unmatchedCount}} unmatched item(s) will be listed as "quoted separately".</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="success" text @click="approve" :loading="approving">Approve &amp; Send</v-btn>
          <v-btn class="error" text @click="approveDialog=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="orderDialog" width="500">
      <v-card>
        <v-card-title class="text-h5">Generate Order</v-card-title>
        <v-card-text style="padding-top: 20px;">
          <p>This will save your changes and create a <b>draft sales order</b> at your branch, prefilled with the <b>{{matchedCount}} matched item(s)</b> from this {{label.toLowerCase()}}.</p>
          <p v-if="unmatchedCount>0" class="orange--text">{{unmatchedCount}} unmatched item(s) will <b>not</b> be included. Resolve them first if they should be on the order.</p>
          <p class="mb-0">You'll be taken to the order to review, adjust and seal it.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="primary" text @click="generateOrder" :loading="generatingOrder">Create Draft Order</v-btn>
          <v-btn class="error" text @click="orderDialog=false" :disabled="generatingOrder">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sendDialog" width="500" :persistent="sending">
      <v-card>
        <v-card-title class="text-h5">Send Quote via WhatsApp</v-card-title>
        <v-card-text style="padding-top: 20px;">
          <p>This saves your changes and sends the current quotation ({{matchedCount}} item(s), total ${{matchedTotal.toFixed(2)}}) to the number below over WhatsApp.</p>
          <v-text-field
              label="Phone number (with country code)"
              placeholder="e.g. 18685551234"
              outlined
              dense
              prefix="+"
              v-model="sendPhone"
              :disabled="sending"
              @keydown.enter="sendQuote"
          />
          <p v-if="unmatchedCount>0" class="orange--text mb-1">{{unmatchedCount}} unmatched item(s) will be listed as "quoted separately".</p>
          <p class="caption grey--text mb-0">The recipient must have messaged the business within the last 24 hours for WhatsApp to deliver a free-form quote.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="primary" text @click="sendQuote" :loading="sending" :disabled="!sendPhone">Send Quote</v-btn>
          <v-btn class="error" text @click="sendDialog=false" :disabled="sending">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackObj.state" :timeout="4000" :color="snackObj.color">
      {{ snackObj.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackObj.state = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
  import axios from 'axios';
  import { mapGetters } from "vuex";
  import VarionScannerButton from './../../components/VarionScannerButton.vue';
  import AttachmentsPanel from './../../components/AttachmentsPanel.vue';
  export default {
    components: {
      VarionScannerButton,
      AttachmentsPanel
    },
    data () {
      return {
        loader: true,
        saving: false,
        approving: false,

        snackObj: {
          state: false,
          color: '',
          text: ''
        },

        data: null,

        matchDialog: false,
        matchIndex: null,
        matchSearchTerm: "",
        matchResults: [],
        matchLoading: false,

        creatingProduct: false,
        createProductForm: {
          name: "",
          sku: "",
          regularPrice: 0
        },

        approveDialog: false,

        orderDialog: false,
        generatingOrder: false,

        sendDialog: false,
        sending: false,
        sendPhone: ""
      }
    },
    async mounted(){
      await this.getData();
    },
    computed: {
      ...mapGetters(['getEndpoint', 'getGlobalValue', 'isAllowed']),
      label(){
        return this.getGlobalValue('VEC_BOOKLIST_LABEL') || "Booklist";
      },
      matchedTotal(){
        if(!this.data || !this.data.items) return 0;
        return this.data.items.filter(x=>x.matched).reduce((acc,x)=>acc+(Number(x.unitPrice||0)*Number(x.qty||0)),0);
      },
      unmatchedCount(){
        if(!this.data || !this.data.items) return 0;
        return this.data.items.filter(x=>!x.matched).length;
      },
      lowConfCount(){
        if(!this.data || !this.data.items) return 0;
        return this.data.items.filter(x=>x.matched && x.confidence==='low').length;
      },
      matchedCount(){
        if(!this.data || !this.data.items) return 0;
        return this.data.items.filter(x=>x.matched).length;
      }
    },
    methods: {
      snack(text, color=""){
        this.snackObj.text = text;
        this.snackObj.state = true;
        this.snackObj.color = color;
      },
      formatDate(d){
        return d ? new Date(d).toLocaleString() : "";
      },
      statusColor(status){
        if(status==="approved") return "success";
        if(status==="rejected") return "error";
        return "orange";
      },
      // theme-aware row tint for unmatched / low-confidence rows. Light-mode uses pale
      // fills; dark-mode uses low-alpha overlays so the (light) row text stays readable.
      rowStyle(item){
        const dark = this.$vuetify.theme.dark;
        if(!item.matched){
          return { backgroundColor: dark ? 'rgba(255,152,0,0.18)' : '#FFF3E0' };
        }
        if(item.matched && item.confidence==='low'){
          return { backgroundColor: dark ? 'rgba(255,214,0,0.14)' : '#FFFDE7' };
        }
        return {};
      },
      async getData(){
        try {
          let res = await axios.get(`${this.getEndpoint}/api/whatsapp/booklists/${this.$route.params.id}`)
          if(res.data.error) throw res.data.error

          this.data = res.data.data
          if(!this.data.items) this.data.items = [];
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.loader = false;
        }
      },
      addItem(){
        this.data.items.push({description: "", qty: 1, matched: false, productId: null, productName: null, sku: null, unitPrice: 0});
      },
      removeItem(i){
        this.data.items.splice(i, 1);
      },
      unmatchItem(i){
        let item = this.data.items[i];
        item.matched = false;
        item.productId = null;
        item.productName = null;
        item.sku = null;
        item.unitPrice = 0;
        this.$set(this.data.items, i, {...item});
      },
      openMatchDialog(i){
        this.matchIndex = i;
        this.matchSearchTerm = this.data.items[i].description || "";
        this.matchResults = [];
        this.createProductForm = {name: this.data.items[i].description || "", sku: "", regularPrice: 0};
        this.matchDialog = true;
        if(this.matchSearchTerm) this.searchProducts();
      },
      closeMatchDialog(){
        this.matchDialog = false;
        this.matchIndex = null;
        this.matchResults = [];
      },
      async searchProducts(){
        try {
          this.matchLoading = true;
          let res = await axios.get(`${this.getEndpoint}/api/whatsapp/productSearch`, {params: {val: this.matchSearchTerm}})
          if(res.data.error) throw res.data.error

          this.matchResults = res.data.data
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.matchLoading = false;
        }
      },
      matchItemToProduct(i, p){
        let item = this.data.items[i];
        item.matched = true;
        item.productId = p.id;
        item.productName = p.name;
        item.sku = p.sku;
        item.unitPrice = Number(p.unitPrice);
        item.confidence = "high"; // a human picked it
        this.$set(this.data.items, i, {...item});
      },
      selectMatch(p){
        this.matchItemToProduct(this.matchIndex, p);
        this.closeMatchDialog();
      },
      applySuggestion(i, s){
        this.matchItemToProduct(i, s);
      },
      confirmMatch(i){
        let item = this.data.items[i];
        item.confidence = "high";
        this.$set(this.data.items, i, {...item});
      },
      openSendDialog(){
        this.sendPhone = this.data.requesterPhone || "";
        this.sendDialog = true;
      },
      async sendQuote(){
        if(!this.sendPhone) return;
        try {
          this.sending = true;
          let saved = await this.save(true);
          if(!saved) return;

          let res = await axios.post(`${this.getEndpoint}/api/whatsapp/booklists/${this.data.id}/sendQuote`, {phone: this.sendPhone})
          if(res.data.error) throw res.data.error

          this.snack(`Quotation sent to ${res.data.data.phone} on WhatsApp 🎉`, "success");
          this.sendDialog = false;
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.sending = false;
        }
      },
      async generateOrder(){
        try {
          this.generatingOrder = true;
          let saved = await this.save(true);
          if(!saved) return;

          let res = await axios.post(`${this.getEndpoint}/api/whatsapp/booklists/${this.data.id}/generateOrder`)
          if(res.data.error) throw res.data.error

          let {orderId, skipped} = res.data.data;
          if(skipped && skipped.length > 0){
            this.snack(`Order #${orderId} created, but ${skipped.length} item(s) were skipped: ${skipped.map(x=>x.description).join(", ")}`, "warning");
          }
          else{
            this.snack(`Draft order #${orderId} created 🎉`, "success");
          }
          this.$router.push({ path: `/orders/view/${orderId}`})
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.generatingOrder = false;
          this.orderDialog = false;
        }
      },
      async createAndMatch(){
        try {
          this.creatingProduct = true;
          let res = await axios.post(`${this.getEndpoint}/api/whatsapp/products/quickCreate`, {
            name: this.createProductForm.name,
            sku: this.createProductForm.sku,
            regularPrice: this.createProductForm.regularPrice
          })
          if(res.data.error) throw res.data.error

          this.snack(`Product "${res.data.data.name}" created and matched 🎉`, "success");
          this.selectMatch(res.data.data);
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.creatingProduct = false;
        }
      },
      async save(silent=false){
        try {
          this.saving = true;
          let res = await axios.put(`${this.getEndpoint}/api/whatsapp/booklists/${this.data.id}`, {
            schoolName: this.data.schoolName,
            grade: this.data.grade,
            items: this.data.items
          })
          if(res.data.error) throw res.data.error

          if(!silent) this.snack(`${this.label} saved 🎉`, "success");
          return true;
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
          return false;
        } finally {
          this.saving = false;
        }
      },
      async approve(){
        try {
          this.approving = true;
          let saved = await this.save(true);
          if(!saved) return;

          let res = await axios.post(`${this.getEndpoint}/api/whatsapp/booklists/${this.data.id}/approve`)
          if(res.data.error) throw res.data.error

          if(res.data.warning) this.snack(res.data.warning, "warning");
          else this.snack(`${this.label} approved and quotation sent 🎉`, "success");
          await this.getData();
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        } finally {
          this.approving = false;
          this.approveDialog = false;
        }
      },
      async reject(){
        try {
          let res = await axios.post(`${this.getEndpoint}/api/whatsapp/booklists/${this.data.id}/reject`)
          if(res.data.error) throw res.data.error

          this.snack(`${this.label} rejected`, "success");
          await this.getData();
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.msg?.message || error, "error");
        }
      }
    }
  }
</script>
<style scoped>
  /* v-chip clips its content by default — let long book names wrap over multiple lines */
  .suggestion-chip {
    height: auto !important;
    min-height: 20px;
    max-width: 100%;
  }
  .suggestion-chip::v-deep .v-chip__content {
    white-space: normal;
    display: inline-block;
    padding: 2px 0;
    word-break: break-word;
  }
</style>
